import type { VoucherCodeResponse } from '@/api/model/voucherCodeResponse';
import { useUseSingleCode } from '@/api/voucher-codes/voucher-codes';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DateTimeDisplay, ValidityStatus } from '@/components/ui/datetime-display';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Download, MapPin, Star } from 'lucide-react';
import React, { useEffect, useState } from 'react';

type OwnedVoucherModalProps = {
  code: VoucherCodeResponse | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const OwnedVoucherModal: React.FC<OwnedVoucherModalProps> = ({ code, open, onOpenChange }) => {
  const [copied, setCopied] = useState(false);
  const [redeemedAt, setRedeemedAt] = useState<string | null>(code?.redeemed_at ?? null);

  const queryClient = useQueryClient();

  const { mutateAsync: markUsed, isPending: isMarking } = useUseSingleCode({
    mutation: {
      onSuccess: () => {
        // Refresh user's voucher list
        queryClient.invalidateQueries({
          queryKey: ['/api/voucher-codes/mine'],
        });
      },
    },
  });

  useEffect(() => {
    setRedeemedAt(code?.redeemed_at ?? null);
  }, [code?.id, code?.redeemed_at]);

  if (!code || !code.voucher) return null;

  const voucher = code.voucher;
  const pdfUrl = code.pdfUrl ?? null;

  const numericValue = typeof voucher.value === 'number' ? voucher.value : Number(voucher.value);

  const formattedValue = Number.isFinite(numericValue)
    ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(numericValue)
    : '';

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleRedeem = async () => {
    await markUsed({ codeId: code.id });
    setRedeemedAt(new Date().toISOString());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='bg-white rounded-2xl p-0 max-w-4xl w-full max-h-[95vh] lg:w-[896px] overflow-hidden border-0 shadow-[0px_6px_20px_0px_rgba(89,225,255,0.1)]'
        showCloseButton={true}
      >
        <div className='flex flex-col lg:flex-row h-full'>
          {/* Left column - Image */}
          <div className='relative w-full lg:w-[448px] h-64 lg:h-full'>
            <div
              className='w-full h-full bg-cover bg-center rounded-t-2xl lg:rounded-l-2xl lg:rounded-tr-none'
              style={{
                backgroundImage: voucher.images?.[0] ? `url(${voucher.images[0]})` : `url('/placeholder.png')`,
              }}
            ></div>
          </div>

          {/* Right column - Details */}
          <div className='flex-1 p-4 lg:p-8 flex flex-col justify-start lg:justify-center gap-6 lg:gap-10 max-w-none overflow-y-auto lg:overflow-y-visible'>
            {/* Header section */}
            <div className='flex flex-col gap-4'>
              <div className='flex flex-col gap-6'>
                <div className='flex flex-col gap-4'>
                  {/* Title and location */}
                  <div className='flex flex-col gap-2'>
                    <h1 className='text-2xl lg:text-[28px] font-semibold text-[#00535f] leading-8 lg:leading-9'>
                      {voucher.title}
                    </h1>
                    <div className='flex flex-col gap-1'>
                      <div className='text-base lg:text-[18px] font-semibold text-[#585858]'>
                        {voucher.provider.name}
                      </div>
                      <div className='flex items-center gap-2 text-[#585858]'>
                        <MapPin className='w-3 h-4 flex-shrink-0' />
                        <span className='text-sm lg:text-[16px] font-medium tracking-[0.64px] leading-5 lg:leading-6'>
                          {voucher.provider.address}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className='flex flex-wrap gap-2 lg:gap-3'>
                    <Badge className='bg-[#d3ed2e] text-[#00535f] text-xs lg:text-[14px] font-bold tracking-[0.56px] leading-4 lg:leading-5 px-2 lg:px-3 py-1 lg:py-1.5 rounded-lg border-0 flex items-center gap-1'>
                      <Star className='w-3 h-3 lg:w-3.5 lg:h-3.5 fill-current' />
                      8.5
                    </Badge>
                    <Badge className='bg-[#d3ed2e] text-[#00535f] text-xs lg:text-[14px] font-bold tracking-[0.56px] leading-4 lg:leading-5 px-2 lg:px-3 py-1 lg:py-1.5 rounded-lg border-0'>
                      {voucher.provider.category}
                    </Badge>
                    {formattedValue && (
                      <Badge className='bg-[#d3ed2e] text-[#00535f] text-xs lg:text-[14px] font-bold tracking-[0.56px] leading-4 lg:leading-5 px-2 lg:px-3 py-1 lg:py-1.5 rounded-lg border-0'>
                        {formattedValue}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Voucher Code Section */}
                <div className='bg-gray-50 rounded-xl p-4 border-2 border-dashed border-gray-300'>
                  <h3 className='text-base lg:text-[18px] font-semibold text-[#585858] leading-5 lg:leading-6 mb-3'>
                    Ihr Gutscheincode
                  </h3>
                  <div className='flex items-center justify-between bg-white rounded-lg p-3 border border-gray-200'>
                    <div className='flex flex-col'>
                      <span className='text-2xl font-mono font-bold text-[#00535f] tracking-wider'>{code.code}</span>
                      <ValidityStatus
                        startDate={voucher.valid_from}
                        endDate={voucher.valid_until}
                        className='text-xs mt-1'
                      />
                    </div>
                    <Button onClick={handleCopy} variant='outline' size='sm' className='ml-3 flex items-center gap-2'>
                      {copied ? (
                        <>
                          <Check className='w-4 h-4' />
                          Kopiert!
                        </>
                      ) : (
                        <>
                          <Copy className='w-4 h-4' />
                          Kopieren
                        </>
                      )}
                    </Button>
                  </div>
                  {pdfUrl && (
                    <Button
                      asChild
                      variant='outline'
                      size='sm'
                      className='mt-3 w-full sm:w-auto flex items-center gap-2'
                    >
                      <a href={pdfUrl} target='_blank' rel='noopener noreferrer'>
                        <Download className='w-4 h-4' /> PDF herunterladen
                      </a>
                    </Button>
                  )}
                </div>

                {/* Description section */}
                <div className='flex flex-col gap-2'>
                  <h3 className='text-base lg:text-[18px] font-semibold text-[#585858] leading-5 lg:leading-6'>
                    Angebotsbeschreibung
                  </h3>
                  <p className='text-sm lg:text-[16px] font-medium text-[#72727a] tracking-[0.64px] leading-5 lg:leading-6'>
                    {voucher.description ||
                      `Genießen Sie ${voucher.title} mit ${voucher.value}% Rabatt auf Ihre gesamte Rechnung. Gültig für alle Speisen und Getränke. Nicht kombinierbar mit anderen Aktionen.`}
                  </p>

                  {/* Valid until and minimum order */}
                  <div className='flex flex-col sm:flex-row gap-4 mt-2'>
                    <div>
                      <span className='text-xs lg:text-[14px] font-medium text-zinc-400 tracking-[0.56px] leading-4 lg:leading-5'>
                        Gültig bis:
                      </span>
                      <div className='text-xs lg:text-[14px] font-bold text-[#585858] tracking-[0.56px] leading-4 lg:leading-5'>
                        <DateTimeDisplay date={voucher.valid_until} format='date-only' locale='de-DE' />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Terms section */}
              <div className='flex flex-col gap-2'>
                <h3 className='text-base lg:text-[18px] font-semibold text-[#585858] leading-5 lg:leading-6'>
                  Einlösebedingungen
                </h3>
                <div className='flex flex-col gap-2 text-xs lg:text-[14px] font-medium text-[#72727a] tracking-[0.56px] leading-4 lg:leading-5'>
                  {voucher.conditions ? (
                    <div className='whitespace-pre-line'>{voucher.conditions}</div>
                  ) : (
                    <>
                      <div>• Pro Person nur ein Gutschein einlösbar</div>
                      <div>• Nicht mit anderen Aktionen kombinierbar</div>
                      <div>• Keine Barauszahlung möglich</div>
                      <div>• Reservierung empfohlen</div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Redeem action */}
            <div className='flex justify-end'>
              <Button
                onClick={handleRedeem}
                disabled={Boolean(redeemedAt) || isMarking}
                className='mt-2 flex items-center gap-2'
              >
                {redeemedAt ? (
                  <>
                    <Check className='w-4 h-4' /> Bereits eingelöst
                  </>
                ) : isMarking ? (
                  'Wird markiert...'
                ) : (
                  'Als eingelöst markieren'
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
